/**
 * Sprint ids — the one place that builds or parses them (track WS2-F4, spec #56513).
 *
 * Top-level `relay_sprints/{docId}` docs ("Model A") share one id space across
 * every workspace, so two workspaces' "sprint 1" would collide. The doc id is
 * therefore:
 *
 *   default workspace      sprint-<n>                 (unchanged, no migration)
 *   every other workspace  <workspaceId>__sprint-<n>
 *
 * The per-workspace key `sprint-<n>` (see sprintKey) is still what people, the
 * `sprint:<n>` tags, the helper and the per-workspace Model B docs
 * (`relay_sprints/{ws}/sprints/{key}`) use; only the top-level doc id gains the
 * workspace prefix. adk-relay's scheduler adopts the same format in WS2-F2.
 */

export const DEFAULT_WORKSPACE_ID = "default";

const SEPARATOR = "__";
const KEY_PREFIX = "sprint-";
const KEY_PATTERN = /^sprint-(\d+)$/;

/** Per-workspace key for sprint `n`: `sprint-<n>`. Used for Model B docs, tags and API paths. */
export function sprintKey(n: number | string): string {
  return `${KEY_PREFIX}${n}`;
}

/** Top-level relay_sprints doc id for sprint `n` in `workspaceId`. */
export function sprintDocId(workspaceId: string, n: number | string): string {
  const key = sprintKey(n);
  return !workspaceId || workspaceId === DEFAULT_WORKSPACE_ID ? key : `${workspaceId}${SEPARATOR}${key}`;
}

/** Inverse of sprintDocId; null when `id` is not a sprint doc id. */
export function parseSprintDocId(id: string): { workspaceId: string; n: number } | null {
  const cut = id.lastIndexOf(SEPARATOR);
  const workspaceId = cut === -1 ? DEFAULT_WORKSPACE_ID : id.slice(0, cut);
  const key = cut === -1 ? id : id.slice(cut + SEPARATOR.length);
  const match = KEY_PATTERN.exec(key);
  if (!match || !workspaceId) return null;
  return { workspaceId, n: Number(match[1]) };
}

/**
 * Top-level doc id for an id as callers send it: either the per-workspace key
 * (`sprint-<n>`) or an already-built doc id. Anything else (for example the
 * generated ids of relay_create_sprint) is returned untouched.
 */
export function resolveSprintDocId(workspaceId: string, idOrKey: string): string {
  const m = KEY_PATTERN.exec(idOrKey);
  return m ? sprintDocId(workspaceId, m[1]) : idOrKey;
}

/** Per-workspace key for a top-level doc id (`ws__sprint-3` → `sprint-3`); other ids pass through. */
export function sprintKeyFromDocId(id: string): string {
  const cut = id.lastIndexOf(SEPARATOR);
  const key = cut === -1 ? id : id.slice(cut + SEPARATOR.length);
  return KEY_PATTERN.test(key) ? key : id;
}
